import { useState } from "react";
import type { ListingTemplateProduct } from "@/lib/listing-templates";
import { TemplatePoweredFooter } from "./shared";
import { imageAt, brandOf, trustSections } from "./template-helpers";

/**
 * Template 5 — Detailed Technical.
 * Structured, information-heavy design for products with variants, dimensions,
 * material, compatibility and detailed specifications.
 */

type TabId = "Overview" | "Specifications" | "Variants" | "Shipping";

export default function DetailedTechnicalTemplate({ product }: { product: ListingTemplateProduct }) {
  const [activeImage, setActiveImage] = useState(0);
  const hero = imageAt(product, activeImage) ?? imageAt(product, 0);
  const brand = brandOf(product);
  const sections = trustSections(product);

  const facts = [
    ["Material", product.material],
    ["Weight", product.weight],
    ["Dimensions", product.dimensions],
    ["Warranty", product.warranty],
  ].filter((item): item is [string, string] => Boolean(item[1]));

  const tabs: { id: TabId; label: string }[] = [
    { id: "Overview", label: "Overview" },
    ...(product.specifications.length ? [{ id: "Specifications" as TabId, label: "Specifications" }] : []),
    ...(product.variants.length ? [{ id: "Variants" as TabId, label: "Variants" }] : []),
    ...(sections.length ? [{ id: "Shipping" as TabId, label: "Shipping & Returns" }] : []),
  ];

  const [activeTab, setActiveTab] = useState<TabId>("Overview");

  return (
    <div className="bg-slate-50 text-slate-800 font-sans">
      {/* Technical header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-mono flex-wrap">
                <span className="px-2 py-0.5 bg-slate-100 rounded">SKU {product.sku}</span>
                <span>{product.condition}</span>
              </div>
              <h1 className="text-2xl font-semibold mt-2 leading-snug break-words">{product.title}</h1>
              {brand && <p className="text-slate-500 text-sm mt-1">by {brand}</p>}
            </div>
            <div className="text-right shrink-0">
              <p className="text-2xl font-bold">{product.price}</p>
              {product.wasPrice && <p className="text-sm text-slate-400 line-through">{product.wasPrice}</p>}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: image + quick facts */}
          <div className="space-y-4">
            {hero && (
              <div className="aspect-square rounded-xl overflow-hidden bg-white border border-slate-200">
                <img src={hero.src} alt={hero.alt} data-testid="template-hero-image" className="w-full h-full object-contain object-center" />
              </div>
            )}
            {product.images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {product.images.slice(0, 5).map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImage(i)}
                    className={`aspect-square rounded-md overflow-hidden border-2 ${
                      activeImage === i ? "border-slate-700" : "border-transparent hover:border-slate-300"
                    }`}
                  >
                    <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
            {facts.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Key facts</h3>
                <dl className="space-y-2 text-sm">
                  {facts.map(([label, value]) => (
                    <div key={label} className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-3">
                      <dt className="text-slate-500 shrink-0">{label}</dt>
                      <dd className="font-medium text-slate-800 sm:text-right break-words min-w-0">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>

          {/* Right: tabbed detail */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit flex-wrap">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
                    activeTab === tab.id ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mt-4 bg-white rounded-xl border border-slate-200 p-6">
              {activeTab === "Overview" && (
                <div className="space-y-5">
                  {product.fullDescription.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Description</h3>
                      <div className="space-y-3 text-slate-600 leading-relaxed text-sm">
                        {product.fullDescription.map((para) => (
                          <p key={para.slice(0, 24)}>{para}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  {product.keyFeatures.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Key features</h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {product.keyFeatures.map((feature) => (
                          <li key={feature} className="flex items-start gap-2">
                            <i className="ri-checkbox-circle-line text-slate-500 mt-0.5" />
                            <span className="break-words">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "Specifications" && (
                <table className="w-full text-sm">
                  <tbody>
                    {product.specifications.map((spec, i) => (
                      <tr key={spec.label} className={i % 2 === 0 ? "bg-slate-50" : ""}>
                        <td className="px-2 sm:px-3 py-2.5 font-medium text-slate-500 w-2/5 sm:w-1/3 border-b border-slate-100 align-top break-words leading-snug">
                          {spec.label}
                        </td>
                        <td className="px-2 sm:px-3 py-2.5 text-slate-800 border-b border-slate-100 align-top break-words leading-snug">
                          {spec.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {activeTab === "Variants" && (
                <div className="space-y-6">
                  {product.variants.map((variant) => (
                    <div key={variant.name}>
                      <h3 className="font-semibold mb-2">{variant.name}</h3>
                      <div className="flex flex-wrap gap-2">
                        {variant.options.map((option, i) => (
                          <span
                            key={option}
                            className={`px-3 py-1.5 text-sm rounded-md border ${
                              i === 0 ? "border-slate-700 bg-slate-800 text-white" : "border-slate-200 text-slate-600"
                            }`}
                          >
                            {option}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-slate-400">
                    {product.variants.reduce((acc, v) => acc * v.options.length, 1)} possible combinations available.
                  </p>
                </div>
              )}

              {activeTab === "Shipping" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-sm">
                  {sections.map((section) => (
                    <div key={section.title}>
                      <h3 className="font-semibold mb-2">{section.title}</h3>
                      <ul className="space-y-2 text-slate-600 leading-relaxed">
                        {section.lines.map((line) => (
                          <li key={line} className="break-words">
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
              <span className="text-sm text-slate-500">
                {product.rating.score.toFixed(1)} / 5 · {product.rating.count} verified reviews
              </span>
              <button className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-lg whitespace-nowrap">
                Add to cart
              </button>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-5">
          <TemplatePoweredFooter
            testId="detailed-technical-footer"
            left={`${brand ?? "Store"} · Technical listing`}
            sku={product.sku}
            className="text-slate-400"
            centerClassName="text-center text-slate-300"
            rightClassName="font-mono sm:text-right"
          />
        </div>
      </footer>
    </div>
  );
}